import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Upload,
  FileText,
  CheckCircle,
  AlertTriangle,
  Download,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { customersAPI } from '../../lib/api';

interface StockUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploadComplete: () => void;
}

interface UploadError {
  row: number;
  errors: Record<string, string[]>;
  data: Record<string, string>;
}

export default function StockUploadDialog({
  open,
  onOpenChange,
  onUploadComplete,
}: StockUploadDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<{
    success: boolean;
    created: number;
    updated: number;
    errors: UploadError[];
    total_errors: number;
  } | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.endsWith('.csv')) {
        setFile(droppedFile);
        setUploadResult(null);
      } else {
        toast.error('Please upload a CSV file');
      }
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    try {
      setUploading(true);
      const response = await customersAPI.bulkUploadStockLevels(file);
      setUploadResult(response.data);

      if (response.data.success && response.data.total_errors === 0) {
        toast.success(`Successfully uploaded: ${response.data.created} created, ${response.data.updated} updated`);
        setTimeout(() => {
          onUploadComplete();
          resetDialog();
        }, 1500);
      } else if (response.data.success) {
        toast.warning(`Upload completed with errors: ${response.data.created} created, ${response.data.updated} updated, ${response.data.total_errors} errors`);
      }
    } catch (err: unknown) {
      console.error('Upload error:', err);
      const axiosErr = err as { response?: { data?: { error?: string } } };
      toast.error(axiosErr.response?.data?.error || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await customersAPI.downloadStockTemplate();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'stock_levels_template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading template:', err);
      toast.error('Failed to download template');
    }
  };

  const resetDialog = () => {
    setFile(null);
    setUploadResult(null);
    setUploading(false);
  };

  const handleClose = () => {
    resetDialog();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload Stock Levels
          </DialogTitle>
          <DialogDescription>
            Upload a CSV file with your current stock levels. Existing entries will be updated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Template Download */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-slate-600" />
              <span className="text-sm">Need a template?</span>
            </div>
            <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>

          {/* Drag and Drop Area */}
          {!uploadResult && (
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? 'border-primary bg-primary/5'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {file ? (
                <div className="flex flex-col items-center gap-2">
                  <FileText className="h-10 w-10 text-primary" />
                  <span className="font-medium">{file.name}</span>
                  <span className="text-sm text-muted-foreground">
                    {(file.size / 1024).toFixed(1)} KB
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFile(null)}
                    className="text-red-600"
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-10 w-10 text-slate-400" />
                  <div>
                    <span className="font-medium">Drop your CSV file here</span>
                    <span className="text-muted-foreground"> or </span>
                    <label className="font-medium text-primary cursor-pointer hover:underline">
                      browse
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                    </label>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    CSV files only
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Upload Progress/Result */}
          {uploading && (
            <div className="flex flex-col items-center gap-3 py-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Uploading and processing...</span>
            </div>
          )}

          {uploadResult && (
            <div className="space-y-4">
              {/* Summary */}
              <div className={`flex items-center gap-3 p-4 rounded-lg ${
                uploadResult.total_errors === 0 ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'
              }`}>
                {uploadResult.total_errors === 0 ? (
                  <CheckCircle className="h-6 w-6 text-green-600" />
                ) : (
                  <AlertTriangle className="h-6 w-6 text-amber-600" />
                )}
                <div>
                  <div className="font-medium">
                    {uploadResult.total_errors === 0 ? 'Upload Successful' : 'Upload Completed with Errors'}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {uploadResult.created} created, {uploadResult.updated} updated
                    {uploadResult.total_errors > 0 && `, ${uploadResult.total_errors} errors`}
                  </div>
                </div>
              </div>

              {/* Errors List */}
              {uploadResult.errors.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-slate-50 px-4 py-2 border-b">
                    <span className="font-medium text-sm">Errors (first 10 shown)</span>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto">
                    {uploadResult.errors.map((error, idx) => (
                      <div key={idx} className="px-4 py-2 border-b last:border-b-0 text-sm">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            Row {error.row}
                          </Badge>
                          <span className="text-muted-foreground">
                            {error.data['part_number'] || 'Unknown'}
                          </span>
                        </div>
                        <div className="text-red-600 mt-1">
                          {Object.entries(error.errors).map(([field, messages]) => (
                            <div key={field}>
                              {field}: {messages.join(', ')}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={handleClose}>
              {uploadResult ? 'Close' : 'Cancel'}
            </Button>
            {!uploadResult && (
              <Button
                onClick={handleUpload}
                disabled={!file || uploading}
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload
                  </>
                )}
              </Button>
            )}
            {uploadResult && uploadResult.total_errors > 0 && (
              <Button onClick={resetDialog}>
                Try Again
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
